import React, {
  useCallback, useMemo, useRef, useState,
} from 'react'
import { useStripe } from '@stripe/react-stripe-js'
import { transformOrderToApplePaySessionData } from '@/services/ApplePay/transformers/transformOrderToApplePaySessionData'
import type { PaymentRequest } from '@stripe/stripe-js'

import {
  applePayNotAvailableMessage,
} from '@/data/applePayMessages'

import type { StandardCart, StandardOrder } from '@/types/ShopFront/CheckoutStandards'
import { getCart } from '@/services/Cart/getCart'
import { getOrder } from '@/services/FramedCheckout/orderServices/getOrder'
import { deleteCart, attemptToRecreateCart } from '@/services/FramedCheckout/orderServices/recreateOrder'
import { error, log } from '@/services/Log'
import { startSession } from '@/services/ApplePay/index'
import { isFeatureEnabled } from '@/helpers/isFeatureEnabled'
import { ApplePaySessionWithStripe } from '@/services/ApplePay/Stripe/startStripeApplePaySession'
import applePayModalContentBuilder from './applePayModalContentBuilder'
import { WalletName } from './walletName'
import { pdpCheckoutEvent } from '../../helpers/GAEvents'
import { DispatchPaymentStatus } from '../../hooks/usePaymentStatus'

const isApplePayEnabled = () => (
  isFeatureEnabled('applePayEnabled')
  && isFeatureEnabled('pdpApplePayCtaEnabled')
)

export type ProductAdder = () => (
  Promise<{
    success: boolean,
    newCart: StandardCart | null,
  }>
)

const prepareNewCheckoutForPDPItem = async ({
  addProductFunction,
}: {
  addProductFunction: ProductAdder
}): Promise<{
  newOrder: StandardOrder
  initialCart: StandardCart | null
}> => {
  const initialCart = await getCart()
  if (initialCart && initialCart.id) {
    await deleteCart(initialCart.id)
  }
  const addPdpItemResponse = await addProductFunction()
  if (!addPdpItemResponse.success || !addPdpItemResponse.newCart) {
    throw new Error('Failed to add PDP item to cart')
  }
  const newOrder = await getOrder({ cart: addPdpItemResponse.newCart })
  return {
    newOrder,
    initialCart,
  }
}

const recreateCart = ({
  setHasOngoingSession,
  setCart,
  prepNewSingleItemCheckoutPromise,
  completionHandlerCalled,
  setCompletionHandlerCalled,
}: {
  setHasOngoingSession: (newVal: boolean) => void
  setCart: (newCart: StandardCart | null) => void
  prepNewSingleItemCheckoutPromise: Promise<{
    newOrder: StandardOrder
    initialCart: StandardCart | null
  }>
  completionHandlerCalled: boolean,
  setCompletionHandlerCalled: (newVal: boolean) => void
}) => async () => {
  try {
    log('recreateCart')
    if (completionHandlerCalled) {
      log('skip recreateCart because it is invoked already')
      return
    }
    setCompletionHandlerCalled(true)
    const { initialCart } = await prepNewSingleItemCheckoutPromise
    log('recreateCart: prepNewSingleItemCheckoutPromise', { initialCart })
    const currentCart = await getCart()
    log('recreateCart: getCart', { currentCart })
    if (currentCart && currentCart.id) {
      await deleteCart(currentCart.id)
    }
    if (initialCart) {
      await attemptToRecreateCart(initialCart.lineItems)
    }
  } catch (err) {
    error(`The PDP Failed to restore original cart: ${String(err)}`, err)
  } finally {
    setHasOngoingSession(false)
    const finalCart = await getCart()
    setCart(finalCart)
    log('recreateCart: finalCart', { finalCart })
  }
}

type PDPApplePaySessionStarterInput = {
  prepNewSingleItemCheckoutPromise: Promise<{
    newOrder: StandardOrder
    initialCart: StandardCart | null
  }>
  beforeEnding: () => Promise<void>
}

const startPdpApplePaySassionWithApple = ({
  prepNewSingleItemCheckoutPromise,
  beforeEnding,
}: PDPApplePaySessionStarterInput) => (
  startSession({
    logPrefix: 'Mini Cart ApplePay Session',
    initializationFunction: async () => {
      await prepNewSingleItemCheckoutPromise
    },
    beforeAbort: beforeEnding,
    beforeRedirectOnSuccess: beforeEnding,
    initialPaymentRequest: transformOrderToApplePaySessionData({
      cart: {
        coupons: [],
      },
      subtotal: 0,
      taxTotal: 0,
      totalDiscount: 0,
      shipments: [],
      grandTotal: 0,
    }),
  })
)

const usePDPApplePayCheckout = ({
  paymentRequest,
  canUseApplePay,
  addProductFunction,
  setCart,
  walletName,
  dispatchPaymentStatus,
}: {
  canUseApplePay: boolean
  paymentRequest: PaymentRequest | null
  addProductFunction: ProductAdder
  setCart: (newCart: StandardCart | null) => void
  walletName: WalletName
  dispatchPaymentStatus?: DispatchPaymentStatus
}) => {
  const stripe = useStripe()
  const ctaRef = useRef<HTMLButtonElement>(null)
  const [modalMessage, setModalMessage] = useState<string>('')
  const [hasOngoingSession, setHasOngoingSession] = useState(false)
  const [completionHandlerCalled, setCompletionHandlerCalled] = useState(false)
  const applePayModalContent = useMemo<React.ReactNode>(() => (
    applePayModalContentBuilder(modalMessage)
  ), [modalMessage])
  const loadingModalTimeoutId = useRef<number | null>(null)
  const startApplePaySession = useCallback(() => {
    if (isApplePayEnabled() && !hasOngoingSession) {
      dispatchPaymentStatus?.({
        type: 'start',
        error: null,
      })
      if (loadingModalTimeoutId.current) {
        clearTimeout(loadingModalTimeoutId.current)
      }
      setHasOngoingSession(true)
      const prepNewSingleItemCheckoutPromise = prepareNewCheckoutForPDPItem({
        addProductFunction,
      })
      if (
        isFeatureEnabled('isStripeApplePayEnabled')
        && isFeatureEnabled('isStripePDPApplePayEnabled')
      ) {
        setCompletionHandlerCalled(false)
        const completionHandler = recreateCart({
          setCart,
          setHasOngoingSession,
          prepNewSingleItemCheckoutPromise,
          completionHandlerCalled,
          setCompletionHandlerCalled,
        })
        Promise.all([
          ApplePaySessionWithStripe({
            stripe,
            paymentRequest,
            sessionInitializationPromise: prepNewSingleItemCheckoutPromise,
            beforeEnding: completionHandler,
            onError: () => {
              if (typeof dispatchPaymentStatus === 'function') {
                dispatchPaymentStatus({
                  type: 'fail',
                  error: 'Unable to process your request, please try again or with a different payment method',
                })
              }
              return completionHandler()
            },
            walletName,
            dispatchPaymentStatus,
          }),
        ]).catch(() => {

        })
      } else {
        Promise.all([
          startPdpApplePaySassionWithApple({
            prepNewSingleItemCheckoutPromise,
            beforeEnding: recreateCart({
              setCart,
              setHasOngoingSession,
              prepNewSingleItemCheckoutPromise,
              completionHandlerCalled,
              setCompletionHandlerCalled,
            }),
          }),
        ]).catch(() => {
          if (typeof dispatchPaymentStatus === 'function') {
            dispatchPaymentStatus({
              type: 'fail',
              error: 'Unable to process your request, please try again or with a different payment method',
            })
          }
        })
      }
    }
  }, [
    stripe,
    hasOngoingSession,
    addProductFunction,
    paymentRequest,
    setCart,
    walletName,
    dispatchPaymentStatus,
    completionHandlerCalled,
  ])

  const onStartCheckout = useCallback(() => {
    if (!canUseApplePay) {
      setModalMessage(applePayNotAvailableMessage)
    } else if (isApplePayEnabled()) {
      startApplePaySession()
      pdpCheckoutEvent(walletName)
    }
  }, [canUseApplePay, setModalMessage, startApplePaySession, walletName])

  return {
    onStartCheckout,
    modalContent: applePayModalContent,
    onCloseModal: () => setModalMessage(''),
    canUseApplePay,
    ctaRef,
  }
}

export default usePDPApplePayCheckout
